import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import {ActivityIndicator, Image, ScrollView} from "react-native";
import FSHeader from "../components/FSHeader";
import Div from "../lib/Div";
import P from "../lib/P";
import MainStyle from "../../MainStyle";
import {StoreContext} from "../components/StoreContextProvider";
import FontAwesome from '@expo/vector-icons/FontAwesome';
import FontAwesome5 from '@expo/vector-icons/FontAwesome5';
import {Bet} from "../../generated/smg_service_pb";
import {
  PartnerKioskUpdate_UserSessionUpdateData_UserSessionState, SessionPlayMode
} from "../../generated/partner_service_pb";
import {FormikProps} from "formik";
import Btn from "../components/Btn";
import {useIsFocused, useNavigation} from "@react-navigation/native";
import PlayModeModal from "../modals/PlayModeModal";
import ShotAmountModal from "../modals/ShotAmountModal";
import WagerCurrencySelector from "../components/WagerCurrencySelector";
import {useWagerCurrency} from "../hooks/useWagerCurrency";
import ServiceClient from "../utils/ServiceClient";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";
import golfClub from "../assets/img/golf-club.png";
import GameIcons from "../assets/img/icons/Game Icons.svg";
import FullSwingInfoModal from "../modals/FullSwingInfoModal";
import ConfirmShotModal from "../modals/ConfirmShotModal";
import {
  confirmReadyToSwingAndRequirePlay, isStaleConfirmReadyToSwingError
} from "../grpc/confirmReadyToSwing";
import WaitingForShotIndicator from "../components/WaitingForShotIndicator";
import AsyncStorage from "@react-native-async-storage/async-storage";
import moment from "moment";
import AgeRestrictionWarning from "components/AgeRestrictionWarning";
import {
  BeforeEachTurnTrackStyle, dateFormat, DisabledBeforeEachTurnTrackStyle, FS_WELCOME_ONBOARDING_PENDING_KEY,
  thumbStyle, WELCOME_BONUS_DISPLAY_AMOUNT
} from "../constants";
import UserAvatarProps from "../components/UserAvatarProps";
import BalanceButton from "../components/BalanceButton";
import GlassButton from "../components/GlassButton";
import Switch from "../components/Switch";
import {useConfirmBeforeEachTurn} from "../hooks/useConfirmBeforeEachTurn";
import {track, trackBetPlaced} from "../services/analytics";
import {create} from "@bufbuild/protobuf";
import {
  AutoPlayBalanceStatus, ConfirmationSettingsSchema, SetPlayerConfirmationSettingsRequestSchema
} from "../../generated/bay_session_shared_pb";
import {
  ConfirmReadyToSwingRequestSchema, RespondToConfirmationRequestSchema
} from "../../generated/player_bay_session_pb";
import {PlayerReadiness} from "../../generated/bay_session_shared_pb";
import {PlayerBaySession_Client} from "../constants";
import {FullSwingInfoModalProps} from "../types";
import FullSwingPayoutBlock from "../components/FullSwingPayoutBlock";
import RoundRobinTurnDisplay from "../components/RoundRobinTurnDisplay";
import {playersFromStatus} from "../utils/roundRobinProjection";
import FullScreenTooltip from "components/FullScreenTooltip";
import {isForceNewPlayer} from "../utils/devOverrides";
import useBaySessionProjectionRestore from "../hooks/useBaySessionProjectionRestore";
import {FULL_SWING_SESSION_ID_STORAGE_KEY} from '../services/fullSwingAuthSessionStorage';

const UserSessionState = PartnerKioskUpdate_UserSessionUpdateData_UserSessionState;

const tooltipSteps: Record<number, { text: string; buttonText: string, target: string }> = {
  1: {
    text: `Thanks for signing up! Here’s ${WELCOME_BONUS_DISPLAY_AMOUNT} in Bonus Cash to get you started.\n\nYou can tap here anytime to manage your wallet, including deposits and withdrawals.`,
    buttonText: 'Next', target: 'wallet'
  },
  2: {
    text: 'Tap here to manage your wallet, including deposits and withdrawals.', buttonText: 'Next', target: 'wallet'
  },
  3: {text: 'Tap here to change your shot amount and adjust play settings.', buttonText: 'Next', target: 'shotAmount'},
  4: {text: 'Current and upcoming target distances are displayed here.', buttonText: 'Got it!', target: 'payout'}
};

type Props = {
  bet?: Bet, formik: FormikProps<{ contestAmount: string }>, playModeChange: (amount: number) => Promise<void> | void,
  saveAutoPlaySettings: (enabled: boolean, amount: number) => Promise<void> | void,
  onCancelBet: (betId: bigint) => Promise<void> | void,
  // SMG-4201: the wait for the next bet has gone on long. The notice and its button show only when the screen that owns
  // the search gives both `showStuckNotice` and `onCheckAgain`.
  showStuckNotice?: boolean, onCheckAgain?: () => void, checkingNextBet?: boolean
};

const emptyInfoModal: FullSwingInfoModalProps = {visible: false, title: '', subtitle: '', errorIcon: false};

const FullSwingHomePage = (props: Props) => {
  const {bet, formik, saveAutoPlaySettings, onCancelBet, showStuckNotice, onCheckAgain, checkingNextBet} = props;
  const Context = useContext(StoreContext);
  const {user, extendedUserData, cancelModalConfig, balance, streamError, userSessionData, roundRobinStatus} = Context;
  // SMG-3484: server-authoritative funding signals. insufficientFundsNoFallback
  // is set on the PLAYER_INSUFFICIENT_FUNDS stream reason; bayAutoPlayBalanceStatus
  // mirrors SessionPlayer.auto_play_balance_status every frame. Either INSUFFICIENT
  // signal surfaces the notice — independent of bet.betState, so an UNAFFORDABLE
  // 'accepted' bet (bay-session debits at COMMIT, not accept) no longer falls
  // through to the stuck "Waiting for next challenge…" spinner.
  const {insufficientFundsNoFallback, bayAutoPlayBalanceStatus} = Context;
  // playersFromStatus projects roundRobinStatus.{playerOrder, participantNames}
  // into the {id, username}[] shape RoundRobinTurnDisplay expects. Memoize so
  // unrelated re-renders don't churn the array reference.
  const rrPlayers = useMemo(() => playersFromStatus(roundRobinStatus), [roundRobinStatus]);
  // SMG-3446/3447: the wager CATEGORY picker controller. resolvedCurrency is
  // the wallet token to stamp on AcceptBet for the player's selected/auto-
  // selected category ('USD' for Real Money, '' for Points → server resolves
  // the points token from venue config + sticky next_wager_currency).
  const wagerCurrency = useWagerCurrency();
  const isUnder18 = user?.dateOfBirth && moment().diff(moment(user.dateOfBirth, dateFormat), 'years') < 18;
  const navigation: NativeStackNavigationProp<any> = useNavigation();
  const [tooltipStep, setTooltipStep] = useState<number>(0);
  const [playModeVisibility, setPlayModeVisibility] = useState(false);
  const [shotAmountVisibility, setShotAmountVisibility] = useState(false);
  const isFreePlay = extendedUserData.isNewPlayer || extendedUserData.freeShotNumber !== extendedUserData.freeShotCount;
  const [amount, setAmount] = useState(0);
  const [isMax, setIsMax] = useState(false);
  const [infoModal, setInfoModal] = useState<FullSwingInfoModalProps>(emptyInfoModal);
  const [isInsufficientFunds, setIsInsufficientFunds] = useState(false);
  const [sessionId, setSessionId] = useState<null | string>('');
  const [showInvalidQrCodeModal, setShowInvalidQrCodeModal] = useState(false);
  // SMG-3481: per-shot confirmation (Single Shot / on_shot) tracking. The
  // bay-session shot gate requires shot_armed_bet_id == bet_id before a shot
  // is admitted; that slot is stamped by RespondToConfirmation(accepted=true)
  // and consumed (cleared) per shot by the settle CAS. armedBetId is the
  // OPTIMISTIC local latch — set the instant the player taps Confirm so the UI
  // flips to "waiting" without a stream round-trip. The server-authoritative
  // truth is Context.bayShotArmedBetId (PlayerState.shot_armed_bet_id), used in
  // isShotArmedForCurrentBet below so the state is correct after a reload.
  const [armedBetId, setArmedBetId] = useState<bigint | null>(null);

  const isFocused = useIsFocused();
  const isBaySession = Context.sessionBackend === 'bay-session';
  const isBayPlayerReconnectRequired = isBaySession && Context.bayPlayerProjectionReady && Context.stationId <= 0;
  const isBaySessionProjectionOperational = Context.bayPlayerProjectionReady && Context.stationId > 0;
  const {
    isRestoring: isBaySessionProjectionRestoring, retry: retryBaySessionProjection,
    timedOut: baySessionProjectionRestoreTimedOut
  } = useBaySessionProjectionRestore({
    enabled: isFocused, credentialReady: Context.bayPlayerCredentialStatus === 'ready',
    isBaySession, projectionReady: Context.bayPlayerProjectionReady,
    retryProjection: Context.retryBayPlayerProjection
  });
  const isBayPlayerCredentialHydrating = isBaySession && Context.bayPlayerCredentialStatus === 'hydrating';
  const isBayPlayerCredentialUnavailable = isBaySession && (Context.bayPlayerCredentialStatus === 'missing' || Context.bayPlayerCredentialStatus === 'error');
  const isWagerAmountUnavailable = isBaySession && (Context.bayPlayerCredentialStatus !== 'ready' || !isBaySessionProjectionOperational);
  const isWagerAmountControlDisabled = isWagerAmountUnavailable && !isBayPlayerCredentialUnavailable && !isBayPlayerReconnectRequired && !baySessionProjectionRestoreTimedOut;
  // SMG-3050 follow-up: single-shot mode is the per-shot confirm setting
  // (bay-session on_shot), INDEPENDENT of engine auto_play_enabled. Derived
  // from Context.confirmationBypassEnabled (which mirrors on_shot via the
  // PlayerState projection: on_shot=true → bypass=false → single-shot ON).
  const isSingleShot = !Context.confirmationBypassEnabled;
  // NOTE (SMG-4387 revert): isAutoPlayOff (`!user?.autoPlayEnabled`) used to be
  // defined here as the disable guard for the play-mode opener. The opener was
  // reverted out of this screen, so the predicate has no remaining caller and is
  // gone with it. If the opener is ever restored, re-derive it — and do NOT
  // reuse isSingleShot for that guard: the two were the same identifier before
  // commit 6dd522ad2, but SMG-3050 redefined isSingleShot as
  // !confirmationBypassEnabled (per-shot confirm), so reusing it inverts the
  // meaning and blocks the opener for exactly the AUTO PLAY population that
  // needs the opt-out.
  //
  // SMG-3481: is the active bet's shot armed? Union of the optimistic local
  // latch (instant feedback on tap) and the server-authoritative
  // shot_armed_bet_id mirrored onto Context (survives reload — the source of
  // truth). bet.betId is a bigint; the wire value is a stringified int64.
  const isShotArmedForCurrentBet = bet !== undefined && (armedBetId === bet.betId || (Context.bayShotArmedBetId !== '' && Context.bayShotArmedBetId === String(bet.betId)));
  // SMG-3970 follow-up: when the server-authoritative confirmation overlay
  // (ActivePlayerConfirmationModal, driven by confirmationPromptOwed →
  // awaiting_confirmation — which now folds in on_shot) is up, it owns the
  // confirm for THIS shot. Suppress the inline per-shot ConfirmShotModal so it
  // does not stack behind the overlay. The inline modal stays the path for
  // subsequent in-turn shots, once awaiting has cleared and the overlay is down.
  const overlayHandlingConfirm = !!Context.activePlayerConfirmationModalVisible;

  const isMaximumNumberOfPlayers = userSessionData?.isSessionFull && userSessionData?.userSessionState === UserSessionState.NOT_JOINED;
  // Active-player username — when round-robin is enabled, the rotation
  // engine pointer is the reliable source. Partner-service's
  // sessionCurrentActivePlayerUsername is derived from a participant-table
  // IsActive scan that can lag the rotation pointer (skip / rotation
  // advance updates the engine pointer immediately but IsActive lifecycle
  // events lag), so reading sessionCurrentActivePlayerUsername directly
  // produces "still says C" symptoms after rotation has moved to D.
  // Fall back to sessionCurrentActivePlayerUsername when round-robin is
  // disabled, since that's the SetActiveUser-driven path where IsActive
  // IS the canonical signal.
  const activePlayerUsername = roundRobinStatus?.enabled ? (roundRobinStatus.participantNames[roundRobinStatus.currentPlayerId] ?? '') : (userSessionData?.sessionCurrentActivePlayerUsername ?? '');
  // The "it's my turn and the server owes me a confirmation" state is
  // surfaced through the ActivePlayerConfirmationModal itself, driven
  // solely by the server's awaiting_confirmation signal (SMG-3031). The
  // detection logic lives off this screen entirely; the only remaining
  // use here is suppressing the misleading "{me} is currently playing"
  // branch when the rotation pointer is on me and Fix A's username
  // derivation has resolved to my own name.
  const isMyRotationTurnPending = !!roundRobinStatus?.enabled && !!user?.userId && roundRobinStatus.currentPlayerId === Number(user.userId) && userSessionData?.userSessionState === UserSessionState.NOT_ACTIVE && !Context.confirmationBypassEnabled;
  // The "{name} is currently playing — tap your name in the player list
  // to switch players" affordance is OPEN-mode only: in ROTATION the
  // server-driven cursor decides who is active, and the user can't
  // tap-to-switch. Showing the simulator-in-use copy in ROTATION
  // (SMG-3054's prior behaviour) misled players into expecting the
  // OPEN-mode interaction. In ROTATION + NOT_ACTIVE the participant
  // list's active-player highlight + the per-turn confirmation modal
  // are the relevant UI; this screen renders no extra info card.
  const isRotationFromStream = userSessionData?.playMode === SessionPlayMode.ROTATION || !!roundRobinStatus?.enabled;
  const notActiveButActivePlayerExists = !isRotationFromStream && userSessionData?.userSessionState === UserSessionState.NOT_ACTIVE && !!activePlayerUsername && !isMyRotationTurnPending;
  const notActiveAndNoActivePlayerExists = !isRotationFromStream && userSessionData?.userSessionState === UserSessionState.NOT_ACTIVE && !activePlayerUsername && !isMyRotationTurnPending;
  // Play-mode derivation:
  //   - ROTATION when:
  //     (a) the projected round-robin status is enabled (legacy
  //         partner-service-driven signal; pre-BSA), OR
  //     (b) the bay-session SessionState stream says
  //         stationPlayMode=ROTATION. The bay-session migration is the
  //         load-bearing source now; in BSA-native mode `roundRobinStatus`
  //         stays null in production (setRoundRobinStatus is only wired
  //         from the E2E test hook).
  //   - SOLO when the bay-session stream says SESSION_PLAY_MODE_SOLO.
  //   - OPEN otherwise.
  //
  // Source column (post-BSA): bay-session SessionState.station_play_mode,
  // mapped through playerStateToUserSessionData (utils/playerStateToUserSessionData.ts).
  const playMode: 'ROTATION' | 'OPEN' | 'SOLO' = roundRobinStatus?.enabled ? 'ROTATION' : userSessionData?.playMode === SessionPlayMode.ROTATION ? 'ROTATION' : userSessionData?.playMode === SessionPlayMode.SOLO ? 'SOLO' : 'OPEN';
  // The CBT (Confirm Before Each Turn) and CBS (Confirm Before Shot)
  // toggles are interactive in any multi-player mode (OPEN or ROTATION).
  // They dim and clamp to false in SOLO since there is nobody else for
  // the per-turn modal to gate against.
  const isConfirmBeforeEachTurnEnabled = playMode !== 'SOLO';
  // CBS = Confirm-Before-Shot. Sourced from
  // StoreContext.confirmationBypassEnabled, which mirrors the per-turn
  // bypass column for the streaming user. Naming the derived value here
  // (rather than re-reading Context inline at every gate site) prevents
  // confusion between "CBS practice mode" and the underlying bypass
  // column — they are the same thing on this client.
  const isPracticeMode = Context.confirmationBypassEnabled;

  const {
    confirmBeforeEachTurn, setConfirmBeforeEachTurn, isLoading: isConfirmBeforeEachTurnLoading
  } = useConfirmBeforeEachTurn();

  const closeWelcomeBonusModal = async () => {
    await AsyncStorage.removeItem(FS_WELCOME_ONBOARDING_PENDING_KEY);
    resetInfoModal();
    // The simulator-in-use effect was suppressed while welcome was visible;
    // re-evaluate now so the warning appears if another player is still
    // active. Skip the tooltip tour in that case — sim-in-use takes the
    // spotlight until the user becomes active.
    if (applySimulatorInUseModal()) return;
    setTooltipStep(1);
  };

  const handleTooltipNext = async () => {
    if (tooltipStep >= Object.keys(tooltipSteps).length) {
      setTooltipStep(0);
      return;
    }

    setTooltipStep(prevState => prevState + 1);
  };

  const resetInfoModal = () => {
    setInfoModal(emptyInfoModal);
  };

  const reconnectToBay = () => {
    resetInfoModal();
    void (async () => {
      try {
        await Context.logout();

      } catch {
        track('Bay Session Reconnect Logout Failed');
        console.error('[FullSwingHomePage] failed to clear the local session before bay reconnect');

      } finally {
        navigation.reset({index: 0, routes: [{name: 'PartnerAuth'}]});
      }
    })();
  };

  const showBayReconnectModal = (kind: 'bay-player-credential-missing' | 'bay-player-not-joined', subtitle: string) => {
    setInfoModal({
      visible: true, kind, title: 'Reconnect to your bay', confirmText: 'Reconnect', subtitle, errorIcon: true,
      onConfirm: reconnectToBay
    });
  };

  const openWagerAmountControl = () => {
    if (Context.bayPlayerCredentialStatus === 'error') {
      setInfoModal({
        visible: true, kind: 'bay-player-credential-error', title: 'Session unavailable', confirmText: 'Try Again',
        subtitle: 'We could not restore your saved bay connection. Please try again.', errorIcon: true,
        onConfirm: () => {
          resetInfoModal();
          void Context.retryBayPlayerCredential();
        }
      });
      return;
    }

    if (Context.bayPlayerCredentialStatus === 'missing') {
      showBayReconnectModal('bay-player-credential-missing', 'Scan the QR code displayed on the simulator to reconnect your session.');
      return;
    }

    if (isBayPlayerReconnectRequired) {
      showBayReconnectModal('bay-player-not-joined', 'Your bay connection is no longer active. Scan the QR code displayed on the simulator to reconnect.');
      return;
    }

    if (baySessionProjectionRestoreTimedOut) {
      setInfoModal({
        visible: true, kind: 'bay-session-restore-timeout', title: 'Entry fee unavailable', confirmText: 'Try Again',
        subtitle: 'We could not restore your bay session. Please try again.', errorIcon: true,
        onConfirm: () => {
          resetInfoModal();
          retryBaySessionProjection();
        }
      });

      return;
    }

    if (!isWagerAmountUnavailable) setShotAmountVisibility(true);
  };

  const closeInfoModal = () => {
    if (infoModal.kind === 'confirm-before-each-turn') {
      track('Confirm Before Each Turn - Warning Cancelled');
    }

    if (infoModal.kind === 'welcome-bonus') {
      closeWelcomeBonusModal().catch(err => console.log('closeWelcomeBonusModal err', err));
      return;
    }

    resetInfoModal();
  };

  const handleDisableConfirmBeforeEachTurn = async () => {
    const success = await setConfirmBeforeEachTurn(false);

    if (success) {
      track('Confirm Before Each Turn - Disabled');
      resetInfoModal();
    }
  };

  const handleConfirmBeforeEachTurn = async () => {
    // In SOLO play mode the toggle is decorative-only — there is nobody
    // else for the per-turn modal to gate against. Tapping should explain
    // that, not silently no-op, so the user understands why nothing
    // happened. (Previously this branch fired in OPEN mode too; SMG-2711
    // moved Open-mode under the same per-turn gate that ROTATION already
    // used, so the toggle is now meaningful in OPEN.)
    if (!isConfirmBeforeEachTurnEnabled) {
      setInfoModal({
        visible: true, kind: 'confirm-before-each-turn-solo-only', title: 'Confirm Before Each Turn',
        subtitle: 'This setting requires more than one player. Add another player to enable per-turn confirmations.',
        showCloseIcon: true
      });
      return;
    }

    if (isConfirmBeforeEachTurnLoading) return;

    const nextValue = !confirmBeforeEachTurn;

    if (!nextValue) {
      track('Confirm Before Each Turn - Warning Shown');

      setInfoModal({
        visible: true, kind: 'confirm-before-each-turn', title: 'Confirm Before Each Turn', warnIcon: true,
        showCloseIcon: true, onConfirm: handleDisableConfirmBeforeEachTurn,
        subtitle: 'Approve play on your mobile device before each turn begins. Once approved, you can take all your shots for that turn without additional confirmations.',
        warningText: 'Warning: If turned off, shots can be taken without your approval - please remain at the simulator to prevent unauthorized play. You can turn this on anytime if you need to step away.'
      });
      return;
    }

    await setConfirmBeforeEachTurn(true);
  };

  useEffect(() => {
    if (!isFocused) return;
    // SMG-3484: surface the insufficient-funds notice whenever the player
    // cannot fund the wager in front of them. Two independent signals:
    //  1. Server-authoritative — PLAYER_INSUFFICIENT_FUNDS
    //     (insufficientFundsNoFallback) or
    //     SessionPlayer.auto_play_balance_status === INSUFFICIENT. This only
    //     flips once the engine attempts to fund a wager, so it is necessary
    //     but not sufficient (it lags the moment the player CAN see they are
    //     short).
    //  2. Client affordability — the wallet is below the active challenge
    //     cost. bay-session debits at COMMIT (not accept), so an
    //     accepted-but-unaffordable challenge ($3 active, $0.50 balance) must
    //     warn immediately. NOT gated on betState — that gate is exactly what
    //     hid the notice while an unaffordable challenge was active. Read
    //     bet.amount directly (not the derived `amount` state, which a
    //     separate effect sets one render later — gating on it would briefly
    //     use the previous bet's cost on a bet change). 0 between challenges
    //     → fall back to the $3 minimum-challenge floor so the
    //     just-settled-can't-continue state still warns.
    const serverSaysInsufficient = insufficientFundsNoFallback || bayAutoPlayBalanceStatus === AutoPlayBalanceStatus.AUTO_PLAY_BALANCE_INSUFFICIENT;
    const challengeCostCents = Number(bet?.amount) || 300;
    // The client heuristic compares against `balance`, which is the real-money
    // (USD) wallet only — it has no points balance. Gate it on the wager being
    // real money so a Points-mode player (by definition the low-USD cohort the
    // Points feature exists to serve) is NOT falsely flagged insufficient on a
    // points challenge they can fully fund. For Points wagers the authoritative
    // server signals (insufficientFundsNoFallback / AUTO_PLAY_BALANCE_INSUFFICIENT)
    // remain the source of truth.
    const isRealMoneyWager = (wagerCurrency.resolvedCurrency ?? 'USD') === 'USD';
    const clientCannotAfford = isRealMoneyWager && !isFreePlay && balance < challengeCostCents;
    setIsInsufficientFunds(serverSaysInsufficient || clientCannotAfford);
  }, [bet?.betId, bet?.amount, isFocused, isFreePlay, balance, insufficientFundsNoFallback, bayAutoPlayBalanceStatus, wagerCurrency.resolvedCurrency]);

  // Show or clear the "Simulator in Use" info modal based on the current
  // session state. Returns true if a modal was shown so callers can tell
  // whether to skip a fallback action (e.g. tooltip tour).
  const applySimulatorInUseModal = (): boolean => {
    if (isMaximumNumberOfPlayers) {
      setInfoModal({
        visible: true, kind: 'simulator-in-use', title: 'Simulator in Use', errorIcon: false,
        subtitle: 'This simulator has reached the maximum number of players. Please remove a player and scan the QR code to start playing!'
      });
      return true;
    }

    if (notActiveButActivePlayerExists) {
      setInfoModal({
        visible: true, kind: 'simulator-in-use', title: 'Simulator in Use', errorIcon: false,
        subtitle: `${activePlayerUsername} is currently playing. When you’re ready to play on the simulator, tap your name in the player list to switch players.`
      });
      return true;
    }

    if (infoModal.kind === 'simulator-in-use') resetInfoModal();
    return false;
  };

  useEffect(() => {
    // Welcome-bonus is the new-player onboarding step and must not be
    // clobbered by a slow-arriving simulator-in-use trigger. Once the user
    // dismisses welcome, `closeWelcomeBonusModal` re-evaluates this state.
    if (infoModal.kind === 'welcome-bonus') return;
    applySimulatorInUseModal();
    // Depends on activePlayerUsername (and isMyRotationTurnPending) so the
    // modal re-evaluates when the rotation pointer rotates — not only
    // when the local user's own session state transitions. Otherwise the
    // skip-to-next-player flow leaves players who were already NOT_ACTIVE
    // staring at a stale "Y is currently playing" message even after Y
    // is no longer the current player.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaximumNumberOfPlayers, userSessionData?.userSessionState, activePlayerUsername, isMyRotationTurnPending]);

  const adjustBet = async (maxValue: boolean, amountValue: number): Promise<boolean> => {
    if (bet === undefined) return false;
    Context.setLoadingOverlay(true);

    try {
      // SMG-3488 / FSG-8821: re-amount via bay-session ResizeWager, NOT the
      // legacy smg-core SmgService.AdjustBet. AdjustBet updated smg_bet only
      // and published no bay-session snapshot, so the shot-amount change never
      // reached the kiosk/simulator paytable. ResizeWager updates the bet and
      // publishes a SessionState frame so the game re-fetches its paytable.
      // amountValue is already the resolved absolute amount in cents (the shot-
      // amount modal sets it to maxShotAmount for the "max" choice), so no -2
      // "use max" sentinel is needed — ResizeWager takes an absolute newAmount.
      const stationId = BigInt(Context.stationId || 0);
      if (!Context.bayPlayerJwt || stationId === 0n) {
        throw new Error('Unable to change the shot amount right now — no active bay session.');
      }
      // SMG-3492: key the resize on the ACTIVE bet's actual wallet token
      // (bridged from SessionPlayer.wagers_by_currency), NOT the next-wager
      // category picker (resolvedCurrency is '' for Points, which would coerce
      // to USD and mismatch a points wager). Falls back to USD for the legacy
      // FullSwing engine-accept bet, which has no wager row and is always USD.
      const currency = Context.bayActiveBetCurrency || 'USD';
      await ServiceClient.resizeWager(stationId, currency, amountValue, Context.bayPlayerJwt);
      // Optimistic display update: ResizeWager re-amounts the bet IN PLACE
      // (same bet_id), so no new-bet event and no bayActiveBetId change will
      // refresh `bet` — without this the displayed amount stayed stale until
      // the next bet was created after a shot. The stream-bridged wager
      // amount (Context.bayActiveBetAmountMinor → BetSlip re-fetch) then
      // converges the full bet object to the server truth; this keeps the
      // user's own resize correct immediately, even if that frame is slow.
      // Runs only after a successful resize — a rejected RPC must leave the
      // display untouched (the catch below shows the error modal instead).
      setAmount(amountValue);
      setIsMax(maxValue);
      Context.getUser().catch(err => console.log('getUser', err));
      return true;

    } catch (err: any) {
      console.log('adjustBet err', err);

      if (err.message) {
        Context.setCancelModalConfig({
          visible: true, title: 'ERROR', subtitle: err.message, rightBtnText: 'OK',
          onResume: () => {
            Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          },
          onClose: () => {
            Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          }
        });
      }

      return false;

    } finally {
      Context.setLoadingOverlay(false);
    }
  };

  const shotAmountChange = async (maxValue: boolean, amountValue: number, singleShotMode: boolean) => {
    setShotAmountVisibility(false);

    if (isFreePlay) return;
    if (!user) return;

    const currentAutoPlay = user.autoPlayEnabled ?? false;
    const selectedPreferenceAmount = maxValue ? -2 : amountValue;
    const currentPreferenceAmount = currentAutoPlay ? user.autoPlayAmount : user.lastBetAmount;
    const preferenceChanged = currentPreferenceAmount !== selectedPreferenceAmount;
    let currentLiveAmount: number | null = null;
    if (bet?.betState === 'open') currentLiveAmount = amount;
    if (bet?.betState === 'accepted') currentLiveAmount = Number(bet.amount);
    const liveAmountChanged = currentLiveAmount !== null && currentLiveAmount !== amountValue;
    const singleShotChanged = isSingleShot !== singleShotMode;

    if (!preferenceChanged && !liveAmountChanged && !singleShotChanged) return;

    // Single-shot mode writes the per-player per-shot confirm setting
    // (on_shot) DIRECTLY. The engine's auto_play_enabled is INDEPENDENT and
    // not touched here — toggling single-shot must not change autoplay.
    // on_shot=true ↔ single-shot ON (skillstrike shows the per-shot accept
    // dialog); on_shot=false ↔ single-shot OFF (skip dialog).
    //
    // Failures here are SURFACED, not swallowed: this toggle is a consent
    // control, and a write that silently no-ops leaves the switch rendering
    // a state the server never accepted (the stream re-emits the server
    // value and the switch snaps back with no explanation). Missing
    // preconditions therefore throw — same contract as the per-turn sibling
    // (useConfirmBeforeEachTurn) — and every failure lands in the error
    // modal plus telemetry. The write stays soft-fail overall: an error
    // never blocks the rest of the amount-change flow, and re-toggling
    // retries.
    if (singleShotChanged) {
      try {
        // BigInt() normalises user.userId whether it arrives as the proto
        // bigint (production) or a plain number (mock/context edges) — both
        // wire requests below carry an int64 field.
        const userId = BigInt(user.userId ?? 0);
        if (userId === 0n) {
          throw new Error('Station / user not yet ready — try again in a moment');
        }

        if (Context.sessionBackend === 'legacy') {
          // Legacy-fallback backend (FullSwingAuth answered
          // STATION_NOT_BAY_SESSION_MANAGED at login): there is NO
          // bayPlayerJwt, so the bay-session write in the other branch is
          // unreachable. Route through partner-service's
          // FullSwingSetPlayerBypassConfirmation instead: it authenticates
          // with the smg session token (server-side act-on-self), persists
          // the durable per-shot preference the enforcement stack reads
          // (smg_user_details.confirm_on_shot_pref → the session-less bet
          // stamp → the StartHit confirmation gate), and updates the legacy
          // display column so the kiosk-stream
          // account_security_bypass_enabled — and therefore this screen's
          // isSingleShot — converges on the next frame. Legacy vocabulary
          // is BYPASS: enabled = !singleShot.
          const partnerSessionId = await AsyncStorage.getItem(FULL_SWING_SESSION_ID_STORAGE_KEY);
          if (!partnerSessionId) {
            throw new Error('Session not ready — please re-scan the QR code');
          }
          await ServiceClient.fullSwingSetPlayerBypassConfirmation(partnerSessionId, userId, !singleShotMode);

        } else {
          const stationId = BigInt(Context.stationId || 0);
          if (stationId === 0n) {
            throw new Error('Station / user not yet ready — try again in a moment');
          }
          if (!Context.bayPlayerJwt) {
            throw new Error('Single Shot mode is unavailable at this station right now — please see staff or try again after re-scanning the QR code');
          }
          const req = create(SetPlayerConfirmationSettingsRequestSchema, {
            stationId, userId, settings: create(ConfirmationSettingsSchema, {onShot: singleShotMode})
          });
          await PlayerBaySession_Client.setPlayerConfirmationSettings(req, {headers: {Authorization: `Bearer ${Context.bayPlayerJwt}`}});
        }

      } catch (err: any) {
        console.warn('single-shot mode toggle failed', err?.message ?? err);
        track('single_shot_toggle_failed', {
          reason: Context.sessionBackend === 'legacy' ? 'legacy_rpc_error'
            : !Context.bayPlayerJwt ? 'no_bay_credential' : 'rpc_error',
          enabled: singleShotMode,
        });
        Context.setCancelModalConfig({
          visible: true, title: 'ERROR',
          subtitle: err?.message ?? 'Could not update Single Shot mode — please try again.',
          rightBtnText: 'OK',
          onResume: () => Context.setCancelModalConfig(prev => ({...prev, visible: false})),
          onClose: () => Context.setCancelModalConfig(prev => ({...prev, visible: false}))
        });
      }
    }

    // The live amount is always concrete cents; the -2 sentinel belongs only
    // to the saved preference.
    if (liveAmountChanged && bet?.betState === 'accepted') {
      const adjusted = await adjustBet(maxValue, amountValue);
      if (!adjusted) return;
    }

    if (bet?.betState === 'open' && (liveAmountChanged || preferenceChanged)) {
      setAmount(amountValue);
      setIsMax(maxValue);
    }

    // Persist only when the saved preference changed. When autoplay is off the
    // backend stores this in LastBetAmount; when it is on, AutoPlayBetAmount is
    // authoritative. Preserve the autoplay flag in both cases.
    if (preferenceChanged) {
      await saveAutoPlaySettings(currentAutoPlay, selectedPreferenceAmount);
    }
  };

  const AcceptBet = () => {
    if (bet === undefined) return;
    Context.setLoadingOverlay(true);

    const {challenges: challengesList} = bet;

    // SMG-3006: migrated from legacy ServiceClient.acceptBet(betId, ...).
    // stationId now comes from Context.stationId (Main.tsx bridges the
    // bay-session PlayerState.current_station_id onto Context on every
    // stream frame). The legacy `isMax ? -2 : amount` sentinel was a
    // smg-core convention that told the server-side to resolve max from
    // user details; bay-session AcceptBet enforces only positive amounts.
    // Resolve max client-side via extendedUserData.maxShotAmount — the
    // same source the kiosk and the betslip card use, so values match.
    const stationId = BigInt(Context.stationId || 0);
    if (stationId === BigInt(0)) {
      console.warn('[FullSwingHomePage] AcceptBet: stationId unavailable on Context.stationId; cannot dispatch via bay-session');
      Context.setLoadingOverlay(false);
      return;
    }

    const challengeId = challengesList?.[0]?.challengeId;
    if (challengeId === undefined) {
      console.warn('[FullSwingHomePage] AcceptBet: challengeId missing on bet — engine may not have filled it');
      Context.setLoadingOverlay(false);
      return;
    }

    const resolvedAmount = isMax ? (Context.extendedUserData?.maxShotAmount ?? amount) : amount;

    // SMG-3447: currency now flows from the category picker / server sticky
    // next_wager_currency instead of a hardcoded 'USD'. resolvedCurrency is
    // 'USD' for Real Money and '' for Points (empty → AcceptBet falls back to
    // the server-side sticky next_wager_currency + venue config, design §4.3).
    // undefined (no selectable category) preserves the legacy USD default.
    const acceptCurrency = wagerCurrency.resolvedCurrency ?? 'USD';
    ServiceClient.acceptBetV2(
      stationId, BigInt(challengeId), resolvedAmount, acceptCurrency, Context.bayPlayerJwt
    ).then((result) => {
      // SMG-4111: covers accepts performed by bay-session, which never reach
      // the engine. Disjoint from the engine's emitter, not a deduped
      // duplicate — see services/analytics.ts.
      if (!result?.wasIdempotentReplay) {
        // Shared emitter — see services/analytics.ts for why the client and the
        // engine are disjoint populations rather than deduped duplicates.
        trackBetPlaced(result);
      }

      // NOTE: wagerAmount is the REQUESTED amount, not the booked one.
      // trackBetPlaced above deliberately reports the server's figure because
      // bay-session overrides it (PRACTICE forces 0). This event predates that
      // and still reports the request, so wagerAmount MUST NOT be summed as
      // handle — use Bet Placed's amount_cents for that.
      track('Challenge Accepted', {
        accept_source: 'manual',
        ...(challengeId !== undefined && {challengeId: Number(challengeId)}),
        ...(resolvedAmount && {wagerAmount: resolvedAmount}),
      });
      Context.getUser().catch(err => console.log('getUser', err));

    }).catch(err => {
      console.log('acceptBet err', err);
      Context.setLoadingOverlay(false);

      if (err.message) {
        Context.setCancelModalConfig({
          visible: true, title: 'ERROR', subtitle: err.message, rightBtnText: 'OK',
          onResume: () => {
            Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          },
          onClose: () => {
            Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          }
        });
      }
    });
  };

  const CancelBet = () => {
    if (bet === undefined) return;
    void onCancelBet(bet.betId);
  };

  // SMG-3481: per-shot confirmation gesture. Single Shot mode (on_shot) means
  // "play one shot at a time … confirm on your phone when you're ready for an
  // official shot" — the player practices, then taps to confirm the official
  // shot. That confirm IS the per-shot arm: RespondToConfirmation(accepted=true)
  // stamps bay_session_state.shot_armed_bet_id = bet_id, the only thing that
  // satisfies the server's per-shot gate. It is mode-agnostic (SOLO / OPEN /
  // EVENT / ROTATION) — confirm-on-shot is independent of confirm-on-turn,
  // which is handled separately by ActivePlayerConfirmationModal. Without this
  // arm the simulator's hit is rejected with shot_confirmation_pending and the
  // bet can never settle (no payout), so the player is stranded.
  const confirmShot = () => {
    if (bet === undefined) return;
    const stationId = BigInt(Context.stationId || 0);
    const bayPlayerJwt = Context.bayPlayerJwt;

    if (!bayPlayerJwt || stationId === BigInt(0)) {
      Context.setCancelModalConfig({
        visible: true, title: 'ERROR', subtitle: 'Session not ready — please reload the app.', rightBtnText: 'OK',
        onResume: () => Context.setCancelModalConfig(prev => ({...prev, visible: false})),
        onClose: () => Context.setCancelModalConfig(prev => ({...prev, visible: false}))
      });
      return;
    }

    Context.setLoadingOverlay(true);
    const targetBetId = bet.betId;
    // SMG-3967: route on WHY the confirm is owed. The per-SHOT arm
    // (RespondToConfirmation) and the walkaway-protection lock
    // (ConfirmReadyToSwing) are INDEPENDENT gates — RespondToConfirmation
    // never clears timeout_locked or lock_status. A gated player tapping
    // "confirm your shot" would otherwise arm the shot while ProcessShot still
    // rejects it. Clear the self-clearable account-readiness gates first;
    // simulator-owned timeouts project PAUSED and never enter this branch.
    const auth = {headers: {Authorization: `Bearer ${bayPlayerJwt}`}};
    const clearLock = async () => {
      if (Context.bayPlayerReadiness !== PlayerReadiness.CONFIRM) return;

      await confirmReadyToSwingAndRequirePlay(() => {
        return PlayerBaySession_Client.confirmReadyToSwing(
          create(ConfirmReadyToSwingRequestSchema, {stationId}),
          auth
        );
      });
    };

    clearLock().then(() => {
      return PlayerBaySession_Client.respondToConfirmation(
        create(RespondToConfirmationRequestSchema, {stationId, accepted: true}), auth
      );

    }).then(() => {
      // Local arm latch — an optimistic per-betId flag for instant feedback on
      // tap, ahead of the next PlayerState frame mirroring shot_armed_bet_id
      // onto Context.bayShotArmedBetId (bayPlayerProjection.ts). isShotArmed-
      // ForCurrentBet (:147) unions both and treats the server mirror as the
      // source of truth (survives reload); a new betId always breaks the latch.
      setArmedBetId(targetBetId);
      Context.setLoadingOverlay(false);

    }).catch(err => {
      console.log('confirmShot err', err);
      Context.setLoadingOverlay(false);

      // The server refused the gesture against the state it holds. Keep the
      // confirm UI in place so the player can retry off the next frame; no
      // server gate was changed.
      if (isStaleConfirmReadyToSwingError(err)) return;

      if (err.message) {
        Context.setCancelModalConfig({
          visible: true, title: 'ERROR', subtitle: err.message, rightBtnText: 'OK',
          onResume: () => Context.setCancelModalConfig(prev => ({...prev, visible: false})),
          onClose: () => Context.setCancelModalConfig(prev => ({...prev, visible: false}))
        });
      }
    });
  };

  useEffect(() => {
    if (user?.autoPlayEnabled) setIsMax(user.autoPlayAmount === -2);
    else setIsMax(user?.lastBetAmount === -2);
  }, [user?.autoPlayEnabled, user?.autoPlayAmount, user?.lastBetAmount]);

  useEffect(() => {
    setAmount(Number(bet?.amount) || 0);
  }, [bet?.amount]);

  useEffect(() => {
    // SMG-3401: the ACTIVE-awaiting-bet gap (settle→re-arm, or the first
    // challenge after join) no longer pins the full-screen dark spinner.
    // That overlay read as a "blank" screen and hid the just-settled shot
    // result; the in-page "waiting for next challenge" panel (the ACTIVE arm
    // of the info-box chain below) now holds that state instead. This
    // supersedes the SMG-3258 overlay UX for this specific case while
    // keeping its other arms intact (NOT_JOINED still shows the QR box).
    //
    // Clearing the overlay on every bet / session-state change still turns
    // OFF the explicit AcceptBet / CancelBet / adjustBet spinner the moment
    // its resulting frame lands (those set the overlay imperatively; the
    // effect only re-runs — and clears — once `bet` or the session state
    // actually changes).
    Context.setLoadingOverlay(false);
  }, [bet, userSessionData?.userSessionState]);

  useEffect(() => {
    if (showInvalidQrCodeModal) {
      setTimeout(() => {
        setInfoModal({
          visible: true, kind: 'qr-invalid', title: 'Session Is No Longer Valid', errorIcon: true,
          subtitle: 'Your session is no longer valid.\nPlease scan the new QR code displayed on the simulator to reconnect.'
        });
      }, 150);
    }
  }, [showInvalidQrCodeModal]);

  useEffect(() => {
    if (streamError === 'error listing participants: context canceled') {
      setShowInvalidQrCodeModal(true);
    }

    if (streamError) console.log('streamError', streamError);
  }, [streamError]);

  useEffect(() => {
    AsyncStorage.getItem(FULL_SWING_SESSION_ID_STORAGE_KEY).then(res => {
      setSessionId(res);
    });
  }, []);

  // SignUp writes a one-shot flag after registration; existing-user logins do not.
  const welcomeBonusEvaluatedRef = useRef(false);
  useEffect(() => {
    if (welcomeBonusEvaluatedRef.current) return;
    welcomeBonusEvaluatedRef.current = true;

    AsyncStorage.getItem(FS_WELCOME_ONBOARDING_PENDING_KEY).then(pending => {
      if (pending || isForceNewPlayer()) {
        setInfoModal({
          visible: true, kind: 'welcome-bonus', title: 'Thanks for signing up!', successIcon: true,
          confirmText: 'Let’s go!', onConfirm: closeWelcomeBonusModal,
          subtitle: `${WELCOME_BONUS_DISPLAY_AMOUNT} in Bonus Cash* is now in your wallet. You’re ready to start playing!`,
          noteText: '*Bonus Cash is for gameplay and cannot be withdrawn immediately. Subject to standard promotional terms.'
        });

        return;
      }
    }).catch(err => console.log('welcome onboarding getItem err', err));
  }, []);

  // SMG-3484: the insufficient-funds notice. Rendered directly below the
  // SOLO/OPEN/ROTATION wager row (not at the bottom of the panel) so the
  // "you can't afford this challenge" message sits with the challenge it
  // refers to. Single definition so the placement has one source of truth.
  const insufficientFundsNotice = (
    <Div mt={2} p={16} gap={16} alignItems="center" rounded={12} borderWidth={3} borderColor="#3369FF"
         bg="rgba(19, 19, 20, 0.80)" mx={16} testID="insufficient-funds-notice">
      <Div position="relative" w={52} h={52} rounded={100} bg="rgba(255, 255, 255, 0.15)"
           justifyContent="center" alignItems="center">
        <FontAwesome5 name="money-bill" size={24} color="white"/>

        <Div position="absolute" w={18} h={18} top={0} right={0} rounded={100} bg="#C73232"
             alignItems="center" justifyContent="center">
          <FontAwesome5 name="exclamation" size={10} color="white"/>
        </Div>
      </Div>

      <P fontFamily="DavisSans-Medium">Insufficient Funds</P>

      <P fontFamily="DavisSans-Regular" ta="center">
        Looks like your wallet is running low - add funds to keep playing!
      </P>

      <Btn row h={56} justifyContent="center" alignItems="center" bg="#3369FF" w="100%"
           onPress={() => navigation.navigate({name: 'account', params: {step: 1}})}>
        <P fontFamily="DavisSans-Medium">Deposit Funds</P>
      </Btn>
    </Div>
  );

  return (
    <>
      <ScrollView contentContainerStyle={MainStyle.flex1} scrollEventThrottle={16}>
        <FSHeader px={20} redesignEnabled/>

        <Div h={56} px={16} alignItems="center" row>
          <UserAvatarProps name={user?.username} size={30}/>
          <P fontFamily="DavisSans-Medium" fs={18} ml={12} flex={1}>
            {user?.username}
            {extendedUserData.skillIndex > 0 ? (
              <P fontFamily="DavisSans-Regular" fs={14} color="#BBBCBF"> ({extendedUserData.skillIndex})</P>
            ) : null}
          </P>
          <BalanceButton/>
        </Div>

        <Div h={2} bg="#252526" mx={16}/>

        <Div h={56} px={16} alignItems="center" row>
          {playMode === 'ROTATION' ? (
            <Image source={GameIcons as any} style={[MainStyle.w_30, MainStyle.h_30]}/>
          ) : (
            <Div w={30} h={30} alignItems="center" justifyContent="center">
              <FontAwesome name={playMode === 'OPEN' ? 'group' : 'user'} size={20} color="white"/>
            </Div>
          )}

          <P fontFamily="DavisSans-Medium" fs={14} ml={11} flex={1}
             color={playMode === 'ROTATION' ? '#46BDFB' : '#EBECF0'}>
            {playMode} PLAY
          </P>

          <GlassButton testID="wager-amount-button" overlayColor="rgba(51, 105, 255, 0.50)" borderColor="#B2CCFF"
                       disabled={isWagerAmountControlDisabled} opacity={isWagerAmountControlDisabled ? 0.55 : 1}
                       onPress={openWagerAmountControl}>
            {isBayPlayerCredentialUnavailable || isBayPlayerReconnectRequired || baySessionProjectionRestoreTimedOut ? (
              <FontAwesome testID="wager-amount-restore-warning" name="exclamation-triangle" size={16} color="#F5B942"/>
            ) : isBayPlayerCredentialHydrating || isBaySessionProjectionRestoring ? (
              <ActivityIndicator
                testID="wager-amount-restore-spinner" size="small" color="#B2CCFF"
                style={[MainStyle.w_16, MainStyle.h_16]}
              />
            ) : (
              <Image source={golfClub} style={[MainStyle.w_16, MainStyle.h_16]}/>
            )}
            <P fontFamily="DavisSans-Medium" ml={4}>${amount / 100}</P>
          </GlassButton>
        </Div>

        {/* SMG-3484: insufficient-funds notice sits directly below the wager
            row — with the challenge the player can't afford — not at the
            bottom of the panel. Gated on !isUnder18 so age-restricted users
            get the AgeRestrictionWarning instead of a "Deposit Funds" CTA
            (age restriction takes precedence, as it does for the rest of the
            panel). */}
        {!isUnder18 && isInsufficientFunds && insufficientFundsNotice}

        <Div h={2} bg="#252526" mx={16}/>

        {/* RoundRobinTurnDisplay is an internal debugging surface, not
            product UI — it self-guards behind the ?show_rotation_debug=1
            dev override (off by default; enabled only in non-shipped envs
            via the runtime allowlist in devOverrides, off in every
            deployed/player env) plus its null-status guard, so no
            conditional is needed at the call site. */}
        <RoundRobinTurnDisplay players={rrPlayers} bet={bet}/>

        <FullSwingPayoutBlock
          bet={bet} formik={formik} maxShotAmount={extendedUserData.maxShotAmount}
          isDisabled={userSessionData?.userSessionState !== UserSessionState.ACTIVE || isInsufficientFunds || showInvalidQrCodeModal}
        />

        <Div h={2} bg="#252526"/>

        <Div row w={295} mx="auto" alignItems="center" opacity={isConfirmBeforeEachTurnEnabled ? 1 : 0.5} py={8}
             pt={16}>
          <P fontFamily="DavisSans-Regular" onPress={handleConfirmBeforeEachTurn}>Lock account after each turn</P>

          <FontAwesome
            name="question-circle-o" size={18} color="#BBBCBF" style={MainStyle.m_l_8}
            onPress={() => {
              setInfoModal({
                visible: true, title: 'Confirm Before Each Turn', warnIcon: true, showCloseIcon: true,
                subtitle: 'Approve play on your mobile device before each turn begins. Once approved, you can take all your shots for that turn without additional confirmations.',
                warningText: 'Warning: If turned off, shots can be taken without your approval—please remain at the simulator to prevent unauthorized play. You can turn this on anytime if you need to step away.'
              });
            }}
          />

          <Div flex={1}/>

          <Switch
            // In SOLO mode the toggle is non-interactive, so the displayed
            // value is clamped to false regardless of the underlying server
            // state. This covers the "user was mid-toggle when the mode
            // flipped to SOLO" case — the visual value follows the mode,
            // not stale local state.
            value={isConfirmBeforeEachTurnEnabled && confirmBeforeEachTurn}
            thumbStyle={thumbStyle} onPress={handleConfirmBeforeEachTurn}
            trackStyle={isConfirmBeforeEachTurnEnabled ? BeforeEachTurnTrackStyle : DisabledBeforeEachTurnTrackStyle}
          />
        </Div>

        {isUnder18 ? <AgeRestrictionWarning/> : (
          // The info-box chain below was originally gated on `bet?.betState`
          // alone, but every arm except ACTIVE is a no-bet informational
          // state — so a player with no bet fell through to a blank panel.
          // SMG-3258: widen for the not-at-a-simulator arm (NOT_JOINED /
          // no session / invalid QR). SMG-3261: also the NOT_ACTIVE
          // who-is-playing arms (another player's turn / no active player).
          // SMG-3401: an ACTIVE player with no bet (the settle→re-arm gap,
          // or the first challenge after join) used to fall through to a
          // blank panel under a full-screen spinner. Reach the ACTIVE arm
          // unconditionally so it can hold the "waiting for next challenge"
          // state instead.
          bet?.betState
          || userSessionData?.userSessionState === UserSessionState.ACTIVE
          || userSessionData?.userSessionState === UserSessionState.NOT_JOINED
          || !sessionId
          || showInvalidQrCodeModal
          || notActiveAndNoActivePlayerExists
          || notActiveButActivePlayerExists
        ) && (
          <>
            {/* SMG-3484: the insufficient-funds notice now renders directly
                below the wager row (above), so when insufficient the bottom
                info-box chain renders nothing — no duplicate notice and no
                play UI the player can't afford to use. */}
            {isInsufficientFunds ? null : (userSessionData?.userSessionState === UserSessionState.NOT_JOINED || !sessionId || showInvalidQrCodeModal) ? (
              <Div mt={2} p={16} gap={16} alignItems="center" rounded={12} borderWidth={3} borderColor="#3369FF"
                   bg="rgba(19, 19, 20, 0.80)" mx={16}>
                <Div position="relative" p={5}>
                  <FontAwesome5 name="info-circle" size={48} color="#3369FF"/>
                  <Div position="absolute" top={15} right={15} bottom={15} left={15} bg="white" zIndex={-1}/>
                </Div>

                <P fontFamily="DavisSans-Regular" ta="center" mb={15}>
                  To play, please connect to the simulator by scanning the QR code.
                </P>

                <P fontFamily="DavisSans-Regular" ta="center">
                  You can still manage your account by tapping the menu button above.
                </P>
              </Div>
            ) : userSessionData?.userSessionState === UserSessionState.ACTIVE ? (
              <Div pt={16} pb={10} gap={36}>
                {/* SMG-4387 REVERTED (v1.2.0 hotfix): the AUTO PLAY / MANUAL
                    opener that stood here has been removed again. It shipped in
                    v1.2.0 by accident and read as dead on the partner skin: the
                    tap DID reach core+engine and auto_play_consent DID flip, but
                    the label never moved, because StoreContextProvider's
                    mergeStreamUserProjection re-derives user.autoPlayEnabled
                    from the bay-session frame and falls back to
                    bay_session_players.auto_continue_enabled (schema DEFAULT
                    TRUE) whenever engine enrichment is absent from that frame.
                    The phone therefore snapped straight back to "AUTO PLAY"
                    after every opt-out.

                    Consequence, deliberately accepted: PlayModeModal is once
                    again orphaned (mounted below, no call site), so on the
                    FullSwing skin there is NO gesture that writes
                    auto_play_consent = false — every other reachable caller of
                    enableAutoPlay grants, echoes, or lives behind OpenBets in
                    the !isFullSwing branch. SMG-4387's consent gate is thus
                    inert on this skin: consent stays TRUE for the whole
                    population and the wallet gate behaves as enrollment-only,
                    i.e. as it did before SMG-4387. Restore this opener only
                    together with a fix for the stream read-back, or the button
                    will look broken again.

                    Note ShotAmountModal's "Single Shot mode" switch is a
                    DIFFERENT axis (bay-session on_shot / confirmationBypass)
                    and is unaffected by this revert. */}

                {/* SMG-3446: Real Money vs Points category picker — renders
                    ONLY when the station offers ≥2 wager categories (the
                    single-availability rule); auto-hides otherwise. */}
                <WagerCurrencySelector/>
                {!bet ? (
                  // SMG-3401: ACTIVE player between bets — the bet has just
                  // settled (activeBetId cleared) and the next one has not
                  // arrived yet, or this is the first challenge after join.
                  // Hold a stable "waiting for next challenge" panel instead
                  // of a blank screen / indefinite full-screen spinner. The
                  // just-settled shot result stays visible in the payout /
                  // targets block above (driven by
                  // userSessionData.previousTargetOutcome, independent of the
                  // now-cleared bet). Mode-agnostic: the gap occurs in SOLO,
                  // OPEN and ROTATION alike, so it is NOT gated on isSingleShot.
                  <Div alignItems="center" gap={8} testID="waiting-for-next-challenge">
                    <ActivityIndicator size="large" color="#39F"/>
                    <P fontFamily="DavisSans-Regular">Waiting for next challenge…</P>
                    {/* SMG-4201: the wait has gone on long. Say so, without promising when it ends, and let the player
                        ask again instead of reloading the page. */}
                    {showStuckNotice && onCheckAgain && (
                      <>
                        <P fontFamily="DavisSans-Regular" ta="center" testID="next-challenge-stuck-notice">
                          We can't find your next challenge yet.
                        </P>
                        <Btn testID="check-next-challenge-btn" h={44} px={24} justifyContent="center" rounded={4}
                             borderWidth={2} borderColor="#EBECF0" disabled={checkingNextBet} onPress={onCheckAgain}
                             opacity={checkingNextBet ? 0.5 : 1}>
                          <P fontFamily="DavisSans-Medium">{checkingNextBet ? 'Checking…' : 'Check again'}</P>
                        </Btn>
                      </>
                    )}
                  </Div>
                ) : (
                  // SMG-4387 (review round 7): the `bet.betState === 'open'`
                  // disjunct. This bet-interaction slot used to be gated on
                  // isSingleShot alone, which is !confirmationBypassEnabled —
                  // a PER-SHOT-CONFIRM axis. That is the right gate for the
                  // 'accepted' arm below (a confirm-bypass player owes no
                  // per-shot confirm, so rendering nothing is correct), but it
                  // is the wrong gate for an OPEN bet.
                  //
                  // An OPEN bet needs a tap in BOTH cohorts. BetSlip's open-bet
                  // fallback exists precisely so an opted-out player's declined
                  // auto-accept is "recoverable in one tap" — the asymmetry
                  // argument the consent gate rests on. With the old gate a
                  // player holding confirmationBypassEnabled = true got the bet
                  // fetched onto this screen and then rendered NOTHING for it:
                  // no play-next-shot-btn, no cbs-play-button, no Cancel. Their
                  // only escape was switching autoplay back ON, undoing the
                  // opt-out this PR exists to make reachable.
                  //
                  // Keyed on the bet STATE rather than by widening isSingleShot,
                  // so the 'accepted' arm's cohort split is untouched.
                  user !== undefined && (isSingleShot || bet?.betState === 'open')
                ) && (
                  <>
                    {bet?.betState === 'accepted' ? (
                      Context.sessionBackend === 'legacy' ? (
                        // SMG-4020 (PR #1385 review): legacy-fallback backend —
                        // the confirm gesture (RespondToConfirmation) needs a
                        // bay player JWT this login path cleared, so a
                        // ConfirmShotModal here could only error ("Session not
                        // ready") in a loop. The server withholds on_shot
                        // enforcement for this cohort
                        // (engine_bet_events_consumer.go) precisely so the
                        // swing still scores without a phone confirm — show
                        // the waiting panel (Cancel stays reachable) instead
                        // of a dead confirm button. Keyed on the EXPLICIT
                        // legacy marker (same discriminator as the toggle's
                        // routing above), never on !isBaySession: an unset
                        // backend must keep the bay confirm flow. Swap back to
                        // the confirm flow with the SMG-4020 arm carrier.
                        <WaitingForShotIndicator onCancel={CancelBet}/>
                      ) : isShotArmedForCurrentBet ? (
                        // Armed — the per-shot gate is satisfied for this bet;
                        // wait for the simulator's official shot.
                        <WaitingForShotIndicator onCancel={CancelBet}/>
                      ) : overlayHandlingConfirm ? (
                        // SMG-3970 follow-up: the server-authoritative
                        // ActivePlayerConfirmationModal overlay is owning this
                        // shot's confirm (awaiting_confirmation now folds in
                        // on_shot). Render nothing inline — the overlay covers
                        // the screen and its confirm arms the shot; stacking the
                        // ConfirmShotModal behind it is redundant. Once the
                        // player confirms, awaiting clears (overlay down) and the
                        // bet becomes armed → WaitingForShotIndicator above.
                        null
                      ) : (
                        // SMG-3481: per-shot confirm owed. Single Shot mode
                        // requires the player to confirm each official shot on
                        // their phone (mode-agnostic). Confirming taps
                        // RespondToConfirmation → arms shot_armed_bet_id so the
                        // server admits the shot; without it the sim's hit is
                        // rejected shot_confirmation_pending and the bet never
                        // settles. This inline modal is the path for subsequent
                        // in-turn shots (awaiting cleared, no overlay).
                        //
                        // SMG-3970: present it as a POP-UP that looks nearly
                        // identical to the per-TURN confirmation modal, instead
                        // of the small inline button #949 shipped — a Single
                        // Shot player was easy to miss ("no confirmation
                        // pop-up"). The confirm action + testID are unchanged.
                        <ConfirmShotModal visible onConfirm={confirmShot} onCancel={CancelBet}/>
                      )
                    ) : isPracticeMode && playMode !== 'SOLO' ? (
                      // CBS (Confirm-Before-Shot) variant: same Accept-
                      // Bet affordance, distinct testID so callers can
                      // tell the two states apart. Replaces — never
                      // co-exists with — the legacy play-next-shot
                      // button in this branch. SOLO mode falls through
                      // to the legacy button because the CBS UX is
                      // meaningless without other players.
                      <Div alignItems="center" gap={2}>
                        <Btn testID="cbs-play-button" w={80} h={80} bg="#39F" rounded={40} justifyContent="center"
                             onPress={AcceptBet}>
                          <FontAwesome5 name="play" size={36} color="white" style={MainStyle.m_l_28}/>
                        </Btn>
                        <P fontFamily="DavisSans-Regular">Tap to play next shot</P>
                      </Div>
                    ) : (
                      <Div alignItems="center" gap={2}>
                        <Btn testID="play-next-shot-btn" w={80} h={80} bg="#39F" rounded={40} justifyContent="center"
                             onPress={AcceptBet}>
                          <FontAwesome5 name="play" size={36} color="white" style={MainStyle.m_l_28}/>
                        </Btn>
                        <P fontFamily="DavisSans-Regular">Tap to play next shot</P>
                      </Div>
                    )}
                  </>
                )}
              </Div>
            ) : isMaximumNumberOfPlayers ? (
              <Div mt={2} p={16} gap={16} alignItems="center" rounded={12} borderWidth={3} borderColor="#3369FF"
                   bg="rgba(19, 19, 20, 0.80)" mx={16}>
                <Div position="relative" p={5}>
                  <FontAwesome5 name="info-circle" size={48} color="#3369FF"/>
                  <Div position="absolute" top={15} right={15} bottom={15} left={15} bg="white" zIndex={-1}/>
                </Div>

                <P fontFamily="DavisSans-Regular" ta="center">
                  This simulator has reached the maximum number of players. Please remove a player and scan the QR code
                  to start playing!
                </P>
              </Div>
            ) : notActiveAndNoActivePlayerExists ? (
              <Div mt={2} p={16} gap={16} alignItems="center" rounded={12} borderWidth={3} borderColor="#3369FF"
                   bg="rgba(19, 19, 20, 0.80)" mx={16}>
                <Div position="relative" p={5}>
                  <FontAwesome5 name="info-circle" size={48} color="#3369FF"/>
                  <Div position="absolute" top={15} right={15} bottom={15} left={15} bg="white" zIndex={-1}/>
                </Div>

                <P fontFamily="DavisSans-Regular" ta="center">
                  There are no active players in the simulator. When you’re ready, tap your name in the player list and
                  confirm here to start playing!
                </P>
              </Div>
            ) : notActiveButActivePlayerExists ? (
              <Div mt={2} p={16} gap={16} alignItems="center" rounded={12} borderWidth={3} borderColor="#3369FF"
                   bg="rgba(19, 19, 20, 0.80)" mx={16}>
                <Div position="relative" p={5}>
                  <FontAwesome5 name="info-circle" size={48} color="#3369FF"/>
                  <Div position="absolute" top={15} right={15} bottom={15} left={15} bg="white" zIndex={-1}/>
                </Div>

                <P fontFamily="DavisSans-Regular" ta="center">
                  {`${activePlayerUsername} is currently playing. `}
                  When you’re ready to play on the simulator, tap your name in the player list to switch players.
                </P>
              </Div>
            ) : null}
          </>
        )}

        <Div flex={1}/>

        <Btn my={16} mx="auto" onPress={() => navigation.navigate('TermsAndConditions')}>
          <P fontFamily="DavisSans-Medium" color="#39F" textDecorationLine="underline">Terms and Conditions</P>
        </Btn>

        <Div h={24}/>

        <PlayModeModal
          visible={playModeVisibility} onRequestClose={() => setPlayModeVisibility(false)} isFreePlay={isFreePlay}
          playModeChange={() => props.playModeChange(amount)}
        />

        <ShotAmountModal
          visible={shotAmountVisibility} onRequestClose={() => setShotAmountVisibility(false)}
          amount={amount} onConfirm={shotAmountChange} isFreePlay={isFreePlay} isMax={isMax}
        />
      </ScrollView>

      <FullSwingInfoModal info={infoModal} onRequestClose={closeInfoModal}/>

      {!!tooltipStep && tooltipSteps[tooltipStep] && (
        <FullScreenTooltip
          text={tooltipSteps[tooltipStep].text} buttonText={tooltipSteps[tooltipStep].buttonText}
          target={tooltipSteps[tooltipStep].target} onNext={handleTooltipNext}
        />
      )}
    </>
  );
};

export default FullSwingHomePage;
